import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Network,
  Search,
  FileDown,
  Share2,
  Building2,
  User,
  ArrowLeft,
  Loader2,
  Briefcase,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { RelationshipGraph } from '@/components/relationship/RelationshipGraph';

const RelationshipVisualizer = React.memo(function RelationshipVisualizer() {
  const { type = '', id = '' } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [entityDetails, setEntityDetails] = useState<{
    id: string;
    name: string;
    type: string;
    subtitle?: string;
    url: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);

  useEffect(() => {
    if (id && type) {
      loadEntityDetails(id, type);
    } else {
      setLoading(false);
    }
  }, [id, type]);

  const loadEntityDetails = async (entityId: string, entityType: string) => {
    setLoading(true);

    try {
      switch (entityType) {
        case 'organization':
          const { data: org, error: orgError } = await supabase
            .from('organizations')
            .select('id, name, industry, tier')
            .eq('id', entityId)
            .single();

          if (orgError) throw orgError;

          setEntityDetails({
            id: org.id,
            name: org.name,
            type: 'organization',
            subtitle: org.industry,
            url: `/organizations/${org.id}`,
          });
          break;

        case 'contact':
          const { data: contact, error: contactError } = await supabase
            .from('contacts')
            .select('id, first_name, last_name, job_title, organization_id, organizations(name)')
            .eq('id', entityId)
            .single();

          if (contactError) throw contactError;

          setEntityDetails({
            id: contact.id,
            name: `${contact.first_name} ${contact.last_name}`,
            type: 'contact',
            subtitle: contact.job_title || contact.organizations?.name || '',
            url: `/contacts/${contact.id}`,
          });
          break;

        case 'opportunity':
          const { data: opp, error: oppError } = await supabase
            .from('opportunities')
            .select('id, name, stage, organization_id, organizations(name)')
            .eq('id', entityId)
            .single();

          if (oppError) throw oppError;

          setEntityDetails({
            id: opp.id,
            name: opp.name,
            type: 'opportunity',
            subtitle: `${opp.stage || 'Unknown stage'} · ${opp.organizations?.name || ''}`,
            url: `/opportunities/${opp.id}`,
          });
          break;

        default:
          // Unsupported entity type
          toast({
            title: 'Error',
            description: `Unsupported entity type: ${entityType}`,
            variant: 'destructive',
          });
          navigate('/');
          break;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not load entity details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);

    try {
      // Search organizations
      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select('id, name, tier')
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      if (orgsError) throw orgsError;

      // Search contacts
      const { data: contacts, error: contactsError } = await supabase
        .from('contacts')
        .select('id, first_name, last_name, job_title, organization_id, organizations(name)')
        .or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%`)
        .limit(5);

      if (contactsError) throw contactsError;

      // Search opportunities
      const { data: opps, error: oppsError } = await supabase
        .from('opportunities')
        .select('id, name, stage')
        .ilike('name', `%${searchQuery}%`)
        .limit(5);

      if (oppsError) throw oppsError;

      // Combine results
      const formattedOrgs =
        orgs?.map((org) => ({
          id: org.id,
          name: org.name,
          type: 'organization',
          subtext: org.tier || 'Organization',
        })) || [];

      const formattedContacts =
        contacts?.map((contact) => ({
          id: contact.id,
          name: `${contact.first_name} ${contact.last_name}`,
          type: 'contact',
          subtext: contact.job_title || contact.organizations?.name || 'Contact',
        })) || [];

      const formattedOpps =
        opps?.map((opp) => ({
          id: opp.id,
          name: opp.name,
          type: 'opportunity',
          subtext: opp.stage || 'Opportunity',
        })) || [];

      setSearchResults([...formattedOrgs, ...formattedContacts, ...formattedOpps]);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Search failed',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    toast({
      title: 'Export',
      description: 'Relationship data exported successfully!',
    });
  };

  const handleShare = () => {
    // Copy the current URL to clipboard
    navigator.clipboard.writeText(window.location.href);

    toast({
      title: 'Link Copied',
      description: 'Relationship visualization link copied to clipboard!',
    });
  };

  const renderEntityIcon = (type: string) => {
    switch (type) {
      case 'organization':
        return <Building2 className="h-5 w-5" />;
      case 'contact':
        return <User className="h-5 w-5" />;
      case 'opportunity':
        return <Briefcase className="h-5 w-5" />;
      default:
        return <Network className="h-5 w-5" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          <Network className="mr-2 h-6 w-6" />
          Relationship Visualizer
        </h1>

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport}>
            <FileDown className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={handleShare}>
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Find Entities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex space-x-2">
                <div className="flex-grow">
                  <Input
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button onClick={handleSearch} disabled={loading}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="border rounded-md divide-y max-h-[300px] overflow-y-auto">
                  {searchResults.map((result) => (
                    <div
                      key={`${result.type}-${result.id}`}
                      className="p-2 hover:bg-muted cursor-pointer"
                      onClick={() => navigate(`/relationships/${result.type}/${result.id}`)}
                    >
                      <div className="flex items-center">
                        {renderEntityIcon(result.type)}
                        <div className="ml-2">
                          <div className="font-medium">{result.name}</div>
                          <div className="text-xs text-muted-foreground">{result.subtext}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {entityDetails && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center">
                  {renderEntityIcon(entityDetails.type)}
                  <span className="ml-2">Current Focus</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <div className="font-medium">{entityDetails.name}</div>
                  </div>

                  {entityDetails.subtitle && (
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        {entityDetails.type === 'organization'
                          ? 'Industry'
                          : entityDetails.type === 'contact'
                            ? 'Job Title'
                            : 'Stage'}
                      </Label>
                      <div>{entityDetails.subtitle}</div>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <div className="capitalize">{entityDetails.type}</div>
                  </div>

                  <Separator />

                  <div className="pt-2">
                    <Button variant="outline" className="w-full" asChild>
                      <a href={entityDetails.url}>View Full Profile</a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Visualization Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Relationship Depth</Label>
                <Select defaultValue="2">
                  <SelectTrigger>
                    <SelectValue placeholder="Select depth" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Level</SelectItem>
                    <SelectItem value="2">2 Levels</SelectItem>
                    <SelectItem value="3">3 Levels</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Layout Type</Label>
                <Select defaultValue="force">
                  <SelectTrigger>
                    <SelectValue placeholder="Select layout" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="force">Force-Directed</SelectItem>
                    <SelectItem value="tree">Hierarchical</SelectItem>
                    <SelectItem value="radial">Radial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Additional settings could go here */}
            </CardContent>
          </Card>
        </div>

        {/* Main content */}
        <div className="md:col-span-3">
          {loading ? (
            <div className="h-[600px] flex flex-col items-center justify-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p>Loading relationship data...</p>
            </div>
          ) : id && type ? (
            <RelationshipGraph
              initialEntityId={id}
              initialEntityType={type as any}
              depth={2}
              className="h-[800px]"
            />
          ) : (
            <Card className="h-[600px] flex flex-col items-center justify-center">
              <CardContent className="text-center py-12">
                <Network className="h-16 w-16 text-muted-foreground mb-4 mx-auto" />
                <h2 className="text-xl font-bold mb-2">Explore Entity Relationships</h2>
                <p className="text-muted-foreground max-w-md mb-6">
                  Search for an organization, contact, or opportunity to visualize their
                  relationships and connections across your CRM data.
                </p>
                <div className="flex max-w-md mx-auto">
                  <Input
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mr-2"
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button onClick={handleSearch}>
                    <Search className="mr-2 h-4 w-4" />
                    Search
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="mt-6 border rounded-md divide-y max-w-md mx-auto text-left">
                    {searchResults.map((result) => (
                      <div
                        key={`${result.type}-${result.id}`}
                        className="p-3 hover:bg-muted cursor-pointer"
                        onClick={() => navigate(`/relationships/${result.type}/${result.id}`)}
                      >
                        <div className="flex items-center">
                          {renderEntityIcon(result.type)}
                          <div className="ml-2">
                            <div className="font-medium">{result.name}</div>
                            <div className="text-xs text-muted-foreground">{result.subtext}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <div className="mt-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>
    </div>
  );
});

export default RelationshipVisualizer;
